import type { FourPillars, PillarPosition } from '../types/pillars';
import type { CrossRelation } from '../types/compatibility';
import { stemIndex, branchIndex } from '../core/sexagenary';
import { COMBINES } from '../relations/heavenly-combine';
import { CLASH_PAIRS } from '../relations/heavenly-clash';
import { SIX_COMBINES, THREE_COMBINES, DIRECTIONAL_COMBINES, HIDDEN_COMBINES } from '../relations/earthly-combine';
import { CLASHES } from '../relations/earthly-clash';
import { TRIPLE_PUNISH_1, TRIPLE_PUNISH_2, MUTUAL_PUNISH } from '../relations/earthly-punish';
import { DESTROY_PAIRS } from '../relations/earthly-destroy';
import { HARM_PAIRS } from '../relations/earthly-harm';
import { WONJIN_SAL, GUIMUN_SAL } from '../sinsal/definitions';
import { EARTHLY_BRANCHES } from '../core/constants';
import { getHiddenStemsWithRatio } from '../core/hidden-stems';

/**
 * 두 사주 간 교차 관계(합충형)를 분석한다.
 *
 * 원국 내부의 관계와 달리 인접성 제약이 없다.
 * person1의 각 기둥 × person2의 각 기둥 조합을 순회한다.
 */
export function analyzeCrossRelations(
  fp1: FourPillars,
  fp2: FourPillars,
  noHour: boolean,
): CrossRelation[] {
  const positions: PillarPosition[] = noHour
    ? ['year', 'month', 'day']
    : ['year', 'month', 'day', 'hour'];

  const relations: CrossRelation[] = [];

  // 1. 천간합: person1 천간 × person2 천간
  for (const pos1 of positions) {
    const sIdx1 = stemIndex(fp1[pos1].stem);
    for (const pos2 of positions) {
      const sIdx2 = stemIndex(fp2[pos2].stem);
      for (const [s1, s2, result] of COMBINES) {
        if ((sIdx1 === s1 && sIdx2 === s2) || (sIdx1 === s2 && sIdx2 === s1)) {
          const [first, second] = sIdx1 === s1
            ? [fp1[pos1].stem, fp2[pos2].stem]
            : [fp2[pos2].stem, fp1[pos1].stem];
          relations.push({
            type: '천간합',
            person1Pillar: pos1,
            person2Pillar: pos2,
            stems: [first, second],
            result,
            isDayPillar: pos1 === 'day' && pos2 === 'day',
            description: `${first}${second}합 → ${result}`,
          });
        }
      }
    }
  }

  // 2. 육합: person1 지지 × person2 지지
  for (const pos1 of positions) {
    const bIdx1 = branchIndex(fp1[pos1].branch);
    for (const pos2 of positions) {
      const bIdx2 = branchIndex(fp2[pos2].branch);
      for (const [b1, b2, result] of SIX_COMBINES) {
        if ((bIdx1 === b1 && bIdx2 === b2) || (bIdx1 === b2 && bIdx2 === b1)) {
          const [first, second] = bIdx1 === b1
            ? [fp1[pos1].branch, fp2[pos2].branch]
            : [fp2[pos2].branch, fp1[pos1].branch];
          relations.push({
            type: '육합',
            person1Pillar: pos1,
            person2Pillar: pos2,
            branches: [first, second],
            ...(result ? { result } : {}),
            isDayPillar: pos1 === 'day' && pos2 === 'day',
            description: result
              ? `${first}${second}합 → ${result}`
              : `${first}${second}합`,
          });
        }
      }
    }
  }

  // 3. 삼합: 두 사주의 지지를 합쳐서 3개 완성 여부 확인
  const allBranches: { idx: number; person: 1 | 2; position: PillarPosition }[] = [];
  for (const pos of positions) {
    allBranches.push({ idx: branchIndex(fp1[pos].branch), person: 1, position: pos });
    allBranches.push({ idx: branchIndex(fp2[pos].branch), person: 2, position: pos });
  }

  for (const [b1, b2, b3, result] of THREE_COMBINES) {
    const found1 = allBranches.filter(b => b.idx === b1);
    const found2 = allBranches.filter(b => b.idx === b2);
    const found3 = allBranches.filter(b => b.idx === b3);

    if (found1.length > 0 && found2.length > 0 && found3.length > 0) {
      // Only count if branches come from both charts
      const persons = new Set([
        ...found1.map(f => f.person),
        ...found2.map(f => f.person),
        ...found3.map(f => f.person),
      ]);
      if (persons.size === 2) {
        // Pick one representative from each group, preferring cross-chart
        const rep1 = found1[0];
        const rep2 = found2[0];
        const rep3 = found3[0];
        relations.push({
          type: '삼합',
          person1Pillar: (rep1.person === 1 ? rep1 : rep2.person === 1 ? rep2 : rep3).position,
          person2Pillar: (rep1.person === 2 ? rep1 : rep2.person === 2 ? rep2 : rep3).position,
          branches: [EARTHLY_BRANCHES[b1], EARTHLY_BRANCHES[b2], EARTHLY_BRANCHES[b3]],
          result,
          isDayPillar: false,
          description: `${EARTHLY_BRANCHES[b1]}${EARTHLY_BRANCHES[b2]}${EARTHLY_BRANCHES[b3]} 삼합 → ${result}`,
        });
      }
    }
  }

  // 3a. 반합: 삼합 왕지를 포함한 2지지가 양쪽 차트에 교차 존재
  for (const [b1, b2, b3, result] of THREE_COMBINES) {
    // b2 = 왕지(子午卯酉)
    const fWang = allBranches.filter(b => b.idx === b2);
    if (fWang.length === 0) continue;

    const fPartners = [
      { idx: b1, found: allBranches.filter(b => b.idx === b1) },
      { idx: b3, found: allBranches.filter(b => b.idx === b3) },
    ];

    // 삼합 성립 시 반합 불필요
    if (fPartners.every(p => p.found.length > 0)) {
      const ps = new Set([...fWang, ...fPartners[0].found, ...fPartners[1].found].map(x => x.person));
      if (ps.size === 2) continue;
    }

    for (const partner of fPartners) {
      if (partner.found.length === 0) continue;
      const ps = new Set([...fWang, ...partner.found].map(x => x.person));
      if (ps.size === 2) {
        // 왕지와 파트너를 서로 다른 person에서 우선 매칭
        const p1Rep = fWang.find(x => x.person === 1) ?? partner.found.find(x => x.person === 1);
        const p2Rep = partner.found.find(x => x.person === 2) ?? fWang.find(x => x.person === 2);
        if (!p1Rep || !p2Rep) continue; // ps.size===2이면 논리적으로 불가능하지만 안전성 확보
        const brA = Math.min(partner.idx, b2);
        const brB = Math.max(partner.idx, b2);
        relations.push({
          type: '반합',
          person1Pillar: p1Rep.position,
          person2Pillar: p2Rep.position,
          branches: [EARTHLY_BRANCHES[brA], EARTHLY_BRANCHES[brB]],
          result,
          isDayPillar: false,
          description: `${EARTHLY_BRANCHES[brA]}${EARTHLY_BRANCHES[brB]} 반합 → ${result}`,
        });
      }
    }
  }

  // 3b. 방합: 같은 방위의 3지지가 양쪽 차트에 교차 존재
  for (const [b1, b2, b3, result] of DIRECTIONAL_COMBINES) {
    const f1 = allBranches.filter(b => b.idx === b1);
    const f2 = allBranches.filter(b => b.idx === b2);
    const f3 = allBranches.filter(b => b.idx === b3);

    if (f1.length > 0 && f2.length > 0 && f3.length > 0) {
      const persons = new Set([...f1, ...f2, ...f3].map(x => x.person));
      if (persons.size === 2) {
        // 각 person에서 상대가 제공하지 않는 지지를 가진 기둥을 우선 선택
        const p1Only = [f1, f2, f3].find(g => g.some(x => x.person === 1) && !g.some(x => x.person === 2));
        const p2Only = [f1, f2, f3].find(g => g.some(x => x.person === 2) && !g.some(x => x.person === 1));
        const rep1 = p1Only?.find(x => x.person === 1) ?? f1.find(x => x.person === 1) ?? f2.find(x => x.person === 1) ?? f3.find(x => x.person === 1);
        const rep2 = p2Only?.find(x => x.person === 2) ?? f1.find(x => x.person === 2) ?? f2.find(x => x.person === 2) ?? f3.find(x => x.person === 2);
        if (!rep1 || !rep2) continue; // persons.size===2이면 논리적으로 불가능하지만 안전성 확보
        relations.push({
          type: '방합',
          person1Pillar: rep1.position,
          person2Pillar: rep2.position,
          branches: [EARTHLY_BRANCHES[b1], EARTHLY_BRANCHES[b2], EARTHLY_BRANCHES[b3]],
          result,
          isDayPillar: false,
          description: `${EARTHLY_BRANCHES[b1]}${EARTHLY_BRANCHES[b2]}${EARTHLY_BRANCHES[b3]} 방합 → ${result}`,
        });
      }
    }
  }

  // 4. 육충: person1 지지 × person2 지지
  for (const pos1 of positions) {
    const bIdx1 = branchIndex(fp1[pos1].branch);
    for (const pos2 of positions) {
      const bIdx2 = branchIndex(fp2[pos2].branch);
      for (const [c1, c2] of CLASHES) {
        if ((bIdx1 === c1 && bIdx2 === c2) || (bIdx1 === c2 && bIdx2 === c1)) {
          const [first, second] = bIdx1 === c1
            ? [fp1[pos1].branch, fp2[pos2].branch]
            : [fp2[pos2].branch, fp1[pos1].branch];
          relations.push({
            type: '육충',
            person1Pillar: pos1,
            person2Pillar: pos2,
            branches: [first, second],
            isDayPillar: pos1 === 'day' && pos2 === 'day',
            description: `${first}${second}충`,
          });
        }
      }
    }
  }

  // 5. 형: 삼형/상형 (cross-chart)
  for (const tripleSet of [TRIPLE_PUNISH_1, TRIPLE_PUNISH_2]) {
    const fromP1 = positions
      .filter(pos => tripleSet.includes(branchIndex(fp1[pos].branch)))
      .map(pos => ({ person: 1 as const, position: pos, idx: branchIndex(fp1[pos].branch) }));
    const fromP2 = positions
      .filter(pos => tripleSet.includes(branchIndex(fp2[pos].branch)))
      .map(pos => ({ person: 2 as const, position: pos, idx: branchIndex(fp2[pos].branch) }));

    // Cross-chart pairs
    for (const a of fromP1) {
      for (const b of fromP2) {
        if (a.idx === b.idx) continue; // Same branch is not a punishment
        const posA = tripleSet.indexOf(a.idx);
        const posB = tripleSet.indexOf(b.idx);
        const [first, second] = posA < posB ? [a, b] : [b, a];
        const allIndices = new Set([...fromP1.map(x => x.idx), ...fromP2.map(x => x.idx)]);
        const uniqueInSet = [...allIndices].filter(idx => tripleSet.includes(idx));
        const relType = uniqueInSet.length >= 3 ? '삼형' : '상형';
        relations.push({
          type: relType,
          person1Pillar: a.position,
          person2Pillar: b.position,
          branches: [
            EARTHLY_BRANCHES[first.idx],
            EARTHLY_BRANCHES[second.idx],
          ],
          isDayPillar: a.position === 'day' && b.position === 'day',
          description: `${EARTHLY_BRANCHES[first.idx]}${EARTHLY_BRANCHES[second.idx]}형`,
        });
      }
    }
  }

  // 6. 자묘형: person1 지지 × person2 지지
  for (const pos1 of positions) {
    const bIdx1 = branchIndex(fp1[pos1].branch);
    for (const pos2 of positions) {
      const bIdx2 = branchIndex(fp2[pos2].branch);
      if ((bIdx1 === MUTUAL_PUNISH[0] && bIdx2 === MUTUAL_PUNISH[1]) ||
          (bIdx1 === MUTUAL_PUNISH[1] && bIdx2 === MUTUAL_PUNISH[0])) {
        const [first, second] = bIdx1 === MUTUAL_PUNISH[0]
          ? [fp1[pos1].branch, fp2[pos2].branch]
          : [fp2[pos2].branch, fp1[pos1].branch];
        relations.push({
          type: '자묘형',
          person1Pillar: pos1,
          person2Pillar: pos2,
          branches: [first, second],
          isDayPillar: pos1 === 'day' && pos2 === 'day',
          description: `${first}${second}형 (무례지형)`,
        });
      }
    }
  }

  // 7. 천간충: person1 천간 × person2 천간
  for (const pos1 of positions) {
    const sIdx1 = stemIndex(fp1[pos1].stem);
    for (const pos2 of positions) {
      const sIdx2 = stemIndex(fp2[pos2].stem);
      for (const [s1, s2] of CLASH_PAIRS) {
        if ((sIdx1 === s1 && sIdx2 === s2) || (sIdx1 === s2 && sIdx2 === s1)) {
          relations.push({
            type: '천간충',
            person1Pillar: pos1,
            person2Pillar: pos2,
            stems: [fp1[pos1].stem, fp2[pos2].stem],
            isDayPillar: pos1 === 'day' && pos2 === 'day',
            description: `${fp1[pos1].stem}${fp2[pos2].stem}충`,
          });
        }
      }
    }
  }

  // 8. 파(破): person1 지지 × person2 지지
  for (const pos1 of positions) {
    const bIdx1 = branchIndex(fp1[pos1].branch);
    for (const pos2 of positions) {
      const bIdx2 = branchIndex(fp2[pos2].branch);
      for (const [d1, d2] of DESTROY_PAIRS) {
        if ((bIdx1 === d1 && bIdx2 === d2) || (bIdx1 === d2 && bIdx2 === d1)) {
          relations.push({
            type: '파',
            person1Pillar: pos1,
            person2Pillar: pos2,
            branches: [fp1[pos1].branch, fp2[pos2].branch],
            isDayPillar: pos1 === 'day' && pos2 === 'day',
            description: `${fp1[pos1].branch}${fp2[pos2].branch}파`,
          });
        }
      }
    }
  }

  // 9. 해(害): person1 지지 × person2 지지
  for (const pos1 of positions) {
    const bIdx1 = branchIndex(fp1[pos1].branch);
    for (const pos2 of positions) {
      const bIdx2 = branchIndex(fp2[pos2].branch);
      for (const [h1, h2] of HARM_PAIRS) {
        if ((bIdx1 === h1 && bIdx2 === h2) || (bIdx1 === h2 && bIdx2 === h1)) {
          relations.push({
            type: '해',
            person1Pillar: pos1,
            person2Pillar: pos2,
            branches: [fp1[pos1].branch, fp2[pos2].branch],
            isDayPillar: pos1 === 'day' && pos2 === 'day',
            description: `${fp1[pos1].branch}${fp2[pos2].branch}해`,
          });
        }
      }
    }
  }

  // 10. 암합: person1 지지 × person2 지지 (정기 천간합 기반)
  for (const pos1 of positions) {
    const bIdx1 = branchIndex(fp1[pos1].branch);
    for (const pos2 of positions) {
      const bIdx2 = branchIndex(fp2[pos2].branch);
      for (const [h1, h2, result] of HIDDEN_COMBINES) {
        if ((bIdx1 === h1 && bIdx2 === h2) || (bIdx1 === h2 && bIdx2 === h1)) {
          const [first, second] = bIdx1 === h1
            ? [fp1[pos1].branch, fp2[pos2].branch]
            : [fp2[pos2].branch, fp1[pos1].branch];
          relations.push({
            type: '암합',
            person1Pillar: pos1,
            person2Pillar: pos2,
            branches: [first, second],
            result,
            isDayPillar: pos1 === 'day' && pos2 === 'day',
            description: `${first}${second} 암합 → ${result}`,
          });
        }
      }
    }
  }

  // 11. 원진살: person1 지지 × person2 지지 (모든 기둥 교차)
  for (const pos1 of positions) {
    const bIdx1 = branchIndex(fp1[pos1].branch);
    for (const pos2 of positions) {
      const bIdx2 = branchIndex(fp2[pos2].branch);
      if (WONJIN_SAL[bIdx1] === bIdx2 || WONJIN_SAL[bIdx2] === bIdx1) {
        relations.push({
          type: '원진',
          person1Pillar: pos1,
          person2Pillar: pos2,
          branches: [fp1[pos1].branch, fp2[pos2].branch],
          isDayPillar: pos1 === 'day' && pos2 === 'day',
          description: `${fp1[pos1].branch}${fp2[pos2].branch} 원진`,
        });
      }
    }
  }

  // 12. 귀문관살: person1 지지 × person2 지지 (모든 기둥 교차)
  for (const pos1 of positions) {
    const bIdx1 = branchIndex(fp1[pos1].branch);
    for (const pos2 of positions) {
      const bIdx2 = branchIndex(fp2[pos2].branch);
      if (GUIMUN_SAL[bIdx1] === bIdx2 || GUIMUN_SAL[bIdx2] === bIdx1) {
        relations.push({
          type: '귀문',
          person1Pillar: pos1,
          person2Pillar: pos2,
          branches: [fp1[pos1].branch, fp2[pos2].branch],
          isDayPillar: pos1 === 'day' && pos2 === 'day',
          description: `${fp1[pos1].branch}${fp2[pos2].branch} 귀문`,
        });
      }
    }
  }

  // 13. 지장간합: 모든 지장간 간 천간합 탐색 (여기/중기/정기 포함, ratio 가중)
  // 기존 암합(정기-정기)과 동일 기둥 쌍의 정기-정기 조합만 제외, 여기/중기 조합은 탐색
  const amhapPillarPairs = new Set(
    relations.filter(r => r.type === '암합')
      .map(r => `${r.person1Pillar}-${r.person2Pillar}`),
  );
  for (const pos1 of positions) {
    const hs1 = getHiddenStemsWithRatio(fp1[pos1].branch);
    const mainStemIdx1 = stemIndex(hs1[hs1.length - 1].stem); // 정기
    for (const pos2 of positions) {
      const hs2 = getHiddenStemsWithRatio(fp2[pos2].branch);
      const mainStemIdx2 = stemIndex(hs2[hs2.length - 1].stem); // 정기
      const isAmhapPair = amhapPillarPairs.has(`${pos1}-${pos2}`);
      for (const h1 of hs1) {
        const sIdx1 = stemIndex(h1.stem);
        for (const h2 of hs2) {
          const sIdx2 = stemIndex(h2.stem);
          for (const [c1, c2, result] of COMBINES) {
            if ((sIdx1 === c1 && sIdx2 === c2) || (sIdx1 === c2 && sIdx2 === c1)) {
              // 기존 암합과 동일 기둥의 정기-정기 조합이면 skip
              if (isAmhapPair && sIdx1 === mainStemIdx1 && sIdx2 === mainStemIdx2) continue;
              const weight = (h1.ratio * h2.ratio) / (30 * 30);
              relations.push({
                type: '지장간합',
                person1Pillar: pos1,
                person2Pillar: pos2,
                stems: [h1.stem, h2.stem],
                result,
                isDayPillar: pos1 === 'day' && pos2 === 'day',
                weight: Math.round(weight * 1000) / 1000,
                description: `${h1.stem}(${fp1[pos1].branch}장간)${h2.stem}(${fp2[pos2].branch}장간)합 → ${result}`,
              });
            }
          }
        }
      }
    }
  }

  return relations;
}
